import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES } from "../mock-data";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * STRATA — Horizontal Stratified Reveals
 *
 * Pure black + champagne ivory (#f4ecd6) + tiny amber accent.
 * The signature mechanic: every section reveals its content through
 * HORIZONTAL STRATA — bands that open from a center line outward,
 * or stack-staggered from bottom to top, exposing photo or text in
 * stratified slices. Like geological cross-section + film slats.
 *
 * NO crowns, watches, jewelry boxes. ONLY motion-as-design.
 *
 *  ACT I   HERO — Image revealed via 10 horizontal strata opening
 *          from center. Names appear over the photo via stratified
 *          letter reveals.
 *  ACT II  PINNED STRATA STORY — Pinned section where 4 photos cycle
 *          via stratified replacement (10 horizontal bands stagger).
 *          Single caption rotates per photo.
 *  ACT III TIMELINE STRATA — Years 2023–2026 displayed as massive
 *          horizontal bars stacked vertically, each containing a
 *          milestone. Bars draw left-to-right on scroll.
 *  ACT IV  PANORAMA — Pinned horizontal panorama of place. Single
 *          ultra-wide image scrolled horizontally. Caption fades in.
 *  ACT V   DATE BANDS — Day + month + year displayed as 3 huge
 *          horizontal bands that slide in opposite directions.
 *          Tabular nums.
 *  ACT VI  REPLY — Bands of form fields with stratified entrance.
 */
export function StrataLayout({ formSlot, program, title, dateTime, location, description, galleryImages }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);

  const d = dateTime ? new Date(dateTime) : null;
  const day = d?.getDate().toString().padStart(2, "0") ?? "--";
  const month = d ? (d.getMonth() + 1).toString().padStart(2, "0") : "--";
  const monthName = d?.toLocaleString("en-US", { month: "long" }) ?? "—";
  const year = d?.getFullYear() ?? 2026;
  const time = d?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) ?? "--:--";
  const weekday = d?.toLocaleString("en-US", { weekday: "long" }).toUpperCase() ?? "—";

  const [firstName, lastName] = title.split("&").map((s) => s.trim());

  const STRATA = 10; // Number of horizontal bands

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.7, easing: (t) => 1 - Math.pow(1 - t, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // ── ACT I: Strata reveal hero ──
      // Each band opens from center outward (translateY toward opposite sides)
      document.querySelectorAll(".st-hero-band").forEach((band, i) => {
        const fromCenter = i - (STRATA - 1) / 2;
        gsap.from(band, {
          scaleY: 0,
          transformOrigin: fromCenter < 0 ? "bottom" : "top",
          duration: 1.4,
          delay: 0.05 + Math.abs(fromCenter) * 0.06,
          ease: "expo.out",
        });
      });

      const heroSplit = new SplitText(".st-hero-title", { type: "chars" });
      gsap.from(heroSplit.chars, { y: 60, opacity: 0, stagger: 0.04, duration: 1.4, ease: "expo.out", delay: 0.8 });
      gsap.from(".st-hero-meta", { y: 20, opacity: 0, stagger: 0.1, duration: 1, ease: "expo.out", delay: 1.4 });
      gsap.from(".st-marquee-t", { x: "100%", duration: 1.6, ease: "expo.out" });
      gsap.from(".st-marquee-b", { x: "-100%", duration: 1.6, ease: "expo.out" });

      // ── ACT II: PINNED STRATA STORY ──
      const storySection = document.querySelector<HTMLElement>(".st-story-section");
      const storyImgs = document.querySelectorAll<HTMLElement>(".st-story-img");
      const storyBands = document.querySelectorAll<HTMLElement>(".st-story-band");
      const storyCap = document.querySelector<HTMLElement>(".st-story-caption");
      const storyCapMeta = document.querySelector<HTMLElement>(".st-story-caption-meta");
      const storyIndex = document.querySelector<HTMLElement>(".st-story-index");

      if (storySection && storyImgs.length > 0) {
        const captions = [
          { c: "A spring evening.",       m: "Istanbul · 2024" },
          { c: "Three cities, one path.", m: "Roma · Paris · İstanbul" },
          { c: "Beneath the stars.",      m: "Çeşme · May 2025" },
          { c: "Today.",                  m: `${monthName} ${day}, ${year}` },
        ];

        ScrollTrigger.create({
          trigger: storySection,
          start: "top top",
          end: `+=${storyImgs.length * 100}%`,
          pin: true,
          scrub: 1,
          onUpdate(self) {
            const progress = self.progress * storyImgs.length;
            const active = Math.min(storyImgs.length - 1, Math.floor(progress));
            const localT = progress - active;

            storyImgs.forEach((img, i) => {
              img.style.opacity = i === active ? "1" : "0";
            });

            // Bands close over the active image to create stratified transition
            storyBands.forEach((band, i) => {
              const bandPhase = (i / STRATA);
              const offsetT = Math.max(0, Math.min(1, (localT - bandPhase * 0.3) * 2));
              // Bands close from center outward as the next photo enters
              if (localT > 0.7) {
                const wipe = (localT - 0.7) / 0.3;
                band.style.transform = `scaleY(${wipe})`;
                band.style.opacity = `${wipe}`;
              } else {
                band.style.transform = `scaleY(0)`;
                band.style.opacity = "0";
              }
            });

            if (storyCap) storyCap.textContent = captions[active].c;
            if (storyCapMeta) storyCapMeta.textContent = captions[active].m;
            if (storyIndex) storyIndex.textContent = `0${active + 1} / 0${storyImgs.length}`;
          },
        });
      }

      // ── ACT III: TIMELINE BARS — draw left-to-right ──
      document.querySelectorAll(".st-timeline-bar").forEach((bar, i) => {
        gsap.from(bar, {
          scaleX: 0,
          transformOrigin: "left center",
          duration: 1.4,
          delay: i * 0.15,
          ease: "expo.out",
          scrollTrigger: { trigger: ".st-timeline-section", start: "top 70%", toggleActions: "play reverse play reverse" },
        });
      });
      gsap.from(".st-timeline-row .st-timeline-text", {
        opacity: 0,
        y: 20,
        stagger: 0.15,
        duration: 1,
        delay: 0.4,
        ease: "expo.out",
        scrollTrigger: { trigger: ".st-timeline-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });
      gsap.from(".st-timeline-title", { y: 30, opacity: 0, duration: 1.2, ease: "expo.out",
        scrollTrigger: { trigger: ".st-timeline-section", start: "top 75%", toggleActions: "play reverse play reverse" } });

      // ── ACT IV: PANORAMA — horizontal pan ──
      const panSection = document.querySelector<HTMLElement>(".st-pan-section");
      const panImg = document.querySelector<HTMLElement>(".st-pan-img");
      if (panSection && panImg) {
        ScrollTrigger.create({
          trigger: panSection,
          start: "top top",
          end: "+=200%",
          pin: true,
          scrub: 1,
          onUpdate(self) {
            const xRange = panImg.scrollWidth - window.innerWidth;
            panImg.style.transform = `translateX(${-self.progress * xRange}px)`;
          },
        });

        gsap.from(".st-pan-text", { y: 30, opacity: 0, stagger: 0.12, duration: 1.2, ease: "expo.out",
          scrollTrigger: { trigger: panSection, start: "top 60%", toggleActions: "play reverse play reverse" } });
      }

      // ── ACT V: DATE BANDS ──
      gsap.from(".st-date-day-band", {
        x: "-100%",
        duration: 1.4,
        ease: "expo.out",
        scrollTrigger: { trigger: ".st-date-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });
      gsap.from(".st-date-month-band", {
        x: "100%",
        duration: 1.4,
        ease: "expo.out",
        scrollTrigger: { trigger: ".st-date-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });
      gsap.from(".st-date-year-band", {
        x: "-100%",
        duration: 1.4,
        ease: "expo.out",
        scrollTrigger: { trigger: ".st-date-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });
      gsap.from(".st-date-meta", { y: 30, opacity: 0, stagger: 0.12, duration: 1.2, ease: "expo.out",
        scrollTrigger: { trigger: ".st-date-section", start: "top 60%", toggleActions: "play reverse play reverse" } });

      // ── REPLY ──
      document.querySelectorAll(".st-form-row").forEach((row, i) => {
        gsap.from(row, {
          x: i % 2 === 0 ? -40 : 40,
          opacity: 0,
          duration: 1, delay: i * 0.1, ease: "expo.out",
          scrollTrigger: { trigger: ".st-form", start: "top 80%", toggleActions: "play reverse play reverse" },
        });
      });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  const timeline = [
    { y: "2023", e: "First meeting",   d: "İstanbul, beneath a spring rain." },
    { y: "2024", e: "Three cities",    d: "Roma, Paris, İstanbul — every street, a chapter." },
    { y: "2025", e: "The vow",         d: "Çeşme, beneath the May stars." },
    { y: year.toString(), e: "Today",  d: `${monthName} ${day} — your presence requested.` },
  ];

  return (
    <main className="relative overflow-x-hidden" style={{ background: "#0a0a0a", color: "#f4ecd6", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,300;0,400;0,500;1,300;1,400&family=JetBrains+Mono:wght@300;400&display=swap');
        @keyframes stMQ_L { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes stMQ_R { from { transform: translateX(-50%); } to { transform: translateX(0); } }
      `}</style>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 px-6 sm:px-12 py-4 flex justify-between items-baseline border-b"
        style={{ background: "rgba(10, 10, 10, 0.75)", backdropFilter: "blur(10px)", borderColor: "rgba(244, 236, 214, 0.1)" }}>
        <span className="text-[10px] uppercase tracking-[0.5em]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          Strata · MMXXVI
        </span>
        <span className="italic text-base tracking-tight hidden sm:inline" style={{ fontFamily: "'Playfair Display', serif" }}>
          {firstName} &amp; {lastName}
        </span>
        <span className="text-[10px] uppercase tracking-[0.5em]" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#d4a04e" }}>
          {day}.{month}.{year}
        </span>
      </header>

      {/* ═══ ACT I: STRATA HERO ════════════════════════════════════════ */}
      <section className="relative h-screen overflow-hidden">
        {/* 10 horizontal bands of the hero image */}
        <div className="absolute inset-0">
          {Array.from({ length: STRATA }).map((_, i) => (
            <div
              key={i}
              className="st-hero-band absolute left-0 right-0 overflow-hidden"
              style={{
                top: `${(i / STRATA) * 100}%`,
                height: `${100 / STRATA}%`,
              }}
            >
              <img
                src={galleryImages[0]}
                alt=""
                className="absolute w-full object-cover"
                style={{
                  top: `-${(i / STRATA) * 100}vh`,
                  height: "100vh",
                  filter: "grayscale(1) contrast(1.05)",
                }}
              />
              {/* Thin band separator */}
              <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: "rgba(0,0,0,0.4)" }} />
            </div>
          ))}
        </div>

        {/* Top marquee */}
        <div className="st-marquee-t absolute top-12 left-0 right-0 h-8 overflow-hidden flex items-center z-10" style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }}>
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase text-white" style={{ animation: "stMQ_L 36s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">
                {title.toUpperCase()} <span style={{ color: "#d4a04e" }}>—</span> STRATIFIED EDITION <span style={{ color: "#d4a04e" }}>—</span> {monthName} {day} {year} <span style={{ color: "#d4a04e" }}>—</span>
              </span>
            ))}
          </div>
        </div>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 z-10">
          <p className="st-hero-meta text-[10px] uppercase tracking-[0.6em] mb-6 text-white" style={{ fontFamily: "'JetBrains Mono', monospace", textShadow: "0 2px 8px rgba(0,0,0,0.7)" }}>
            — A stratified invitation —
          </p>

          <h1 className="st-hero-title text-center italic font-light leading-[0.85] tracking-[-0.04em] text-white"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(4rem, 13vw, 11rem)",
              textShadow: "0 4px 30px rgba(0,0,0,0.5)",
            }}>
            {firstName}<br /><span className="not-italic opacity-70" style={{ fontSize: "0.6em" }}>&amp;</span><br />{lastName}
          </h1>

          <div className="st-hero-meta mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.5em] text-white/90" style={{ fontFamily: "'JetBrains Mono', monospace", textShadow: "0 2px 4px rgba(0,0,0,0.6)" }}>
            <span>{monthName}</span>
            <span style={{ color: "#d4a04e" }}>—</span>
            <span className="tabular-nums">{day}</span>
            <span style={{ color: "#d4a04e" }}>—</span>
            <span>{year}</span>
          </div>
        </div>

        {/* Bottom corners */}
        <div className="absolute bottom-16 left-6 sm:left-12 z-10 text-[10px] uppercase tracking-[0.5em] text-white/80" style={{ fontFamily: "'JetBrains Mono', monospace", textShadow: "0 2px 4px rgba(0,0,0,0.6)" }}>
          {weekday}<br /><span className="tabular-nums">{time} CET</span>
        </div>
        <div className="absolute bottom-16 right-6 sm:right-12 z-10 text-right text-[10px] uppercase tracking-[0.5em] text-white/80" style={{ fontFamily: "'JetBrains Mono', monospace", textShadow: "0 2px 4px rgba(0,0,0,0.6)" }}>
          {(location ?? "").split(",")[0]}<br />Black-tie
        </div>

        {/* Bottom marquee */}
        <div className="st-marquee-b absolute bottom-0 left-0 right-0 h-8 overflow-hidden flex items-center z-10" style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }}>
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase" style={{ color: "#d4a04e", animation: "stMQ_R 40s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">— SCROLL TO ADVANCE — 06 ACTS — CHAPTERS I — VI —</span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT II: PINNED STRATA STORY ═══════════════════════════════ */}
      <section className="st-story-section relative h-screen overflow-hidden bg-black">
        <div className="absolute top-32 left-6 sm:left-12 z-20 text-[10px] uppercase tracking-[0.4em] text-white/60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          II — Chapters
        </div>
        <div className="absolute top-32 right-6 sm:right-12 z-20 text-[10px] uppercase tracking-[0.4em] text-white/60 tabular-nums" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          <span className="st-story-index">01 / 04</span>
        </div>

        {/* Photos cross-fade */}
        {galleryImages.slice(0, 4).map((src, i) => (
          <div key={i} className="st-story-img absolute inset-0" style={{ opacity: i === 0 ? 1 : 0, transition: "opacity 0.4s" }}>
            <img src={src} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(1) contrast(1.05)" }} />
          </div>
        ))}

        {/* Stratified transition bands (close + open during photo swap) */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: STRATA }).map((_, i) => (
            <div
              key={i}
              className="st-story-band absolute left-0 right-0"
              style={{
                top: `${(i / STRATA) * 100}%`,
                height: `${100 / STRATA}%`,
                background: "#0a0a0a",
                transform: "scaleY(0)",
                transformOrigin: i < STRATA / 2 ? "top" : "bottom",
                opacity: 0,
              }}
            />
          ))}
        </div>

        {/* Caption */}
        <div className="absolute bottom-16 left-6 sm:left-12 right-6 sm:right-12 z-20 text-white text-center">
          <h3 className="st-story-caption italic font-light leading-tight mb-3"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)", textShadow: "0 4px 20px rgba(0,0,0,0.6)" }}>
            A spring evening.
          </h3>
          <p className="st-story-caption-meta text-[10px] uppercase tracking-[0.5em] text-white/80" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            Istanbul · 2024
          </p>
        </div>
      </section>

      {/* ═══ ACT III: TIMELINE BARS ════════════════════════════════════ */}
      <section className="st-timeline-section relative py-32 px-6 sm:px-12">
        <div className="max-w-6xl mx-auto">
          <p className="st-timeline-title text-[10px] uppercase tracking-[0.5em] mb-3 opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            III — Timeline
          </p>
          <h2 className="st-timeline-title italic font-light leading-tight mb-16"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
            From {timeline[0].y} to {year}.
          </h2>

          <div className="space-y-8">
            {timeline.map((t, i) => (
              <div key={i} className="st-timeline-row grid grid-cols-12 gap-4 items-baseline">
                <div className="col-span-12 sm:col-span-2 st-timeline-text">
                  <p className="font-light tabular-nums leading-none" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 4vw, 3.5rem)", color: i === timeline.length - 1 ? "#d4a04e" : "#f4ecd6" }}>
                    {t.y}
                  </p>
                </div>
                <div className="col-span-12 sm:col-span-3 st-timeline-text">
                  <p className="italic" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.25rem, 2vw, 1.75rem)" }}>
                    {t.e}
                  </p>
                </div>
                <div className="col-span-12 sm:col-span-7 relative">
                  <div className="relative h-px mb-3" style={{ background: "rgba(244, 236, 214, 0.15)" }}>
                    <div
                      className="st-timeline-bar absolute inset-0"
                      style={{ background: i === timeline.length - 1 ? "#d4a04e" : "#f4ecd6", height: "2px", top: "-1px" }}
                    />
                  </div>
                  <p className="st-timeline-text italic text-base opacity-75 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {t.d}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Aggregate stats */}
          <div className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t" style={{ borderColor: "rgba(244, 236, 214, 0.1)" }}>
            {[
              { n: "3", l: "Years" },
              { n: "3", l: "Cities" },
              { n: "1095", l: "Days" },
              { n: "1", l: "Vow" },
            ].map((s, i) => (
              <div key={i} className="st-timeline-text">
                <p className="font-light tabular-nums leading-none" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 4vw, 3.5rem)", color: "#d4a04e" }}>
                  {s.n}
                </p>
                <p className="text-[10px] uppercase tracking-[0.5em] mt-2 opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {s.l}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT IV: PANORAMA ══════════════════════════════════════════ */}
      <section className="st-pan-section relative h-screen overflow-hidden bg-black">
        <div className="absolute top-32 left-6 sm:left-12 z-20 text-[10px] uppercase tracking-[0.4em] text-white/60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          IV — Panorama
        </div>
        <div className="absolute top-32 right-6 sm:right-12 z-20 text-[10px] uppercase tracking-[0.4em] tabular-nums" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#d4a04e" }}>
          The Place
        </div>

        {/* Ultra-wide image (scrolled horizontally) */}
        <div className="absolute top-0 left-0 h-full" style={{ width: "300vw" }}>
          <div className="st-pan-img absolute top-0 left-0 h-full flex" style={{ width: "300vw" }}>
            <img src={galleryImages[4]} alt="" className="h-full w-screen object-cover flex-shrink-0" style={{ filter: "grayscale(1) contrast(1.05)" }} />
            <img src={galleryImages[1]} alt="" className="h-full w-screen object-cover flex-shrink-0" style={{ filter: "grayscale(1) contrast(1.05)" }} />
            <img src={galleryImages[2]} alt="" className="h-full w-screen object-cover flex-shrink-0" style={{ filter: "grayscale(1) contrast(1.05)" }} />
          </div>
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 z-10" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.4), transparent 30%, transparent 70%, rgba(0,0,0,0.7))" }} />

        {/* Caption bottom */}
        <div className="absolute bottom-16 left-6 sm:left-12 right-6 sm:right-12 z-20 text-white">
          <p className="st-pan-text text-[10px] uppercase tracking-[0.5em] mb-4 opacity-80" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#d4a04e" }}>
            — The Place —
          </p>
          <h2 className="st-pan-text italic font-light leading-[0.95] tracking-tight mb-4 max-w-3xl"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
            {(location ?? "").split(",")[0]}
          </h2>
          <p className="st-pan-text italic text-base opacity-80 max-w-md mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {(location ?? "").split(",").slice(1).join(",").trim()}
          </p>
          <p className="st-pan-text text-[10px] uppercase tracking-[0.5em] opacity-70 tabular-nums" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {day}.{month}.{year} · {time} CET · Black-tie
          </p>
        </div>

        {/* Scroll hint */}
        <div className="absolute top-1/2 right-6 sm:right-12 -translate-y-1/2 z-20 text-[10px] uppercase tracking-[0.5em] text-white/40 [writing-mode:vertical-rl]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          Scroll →
        </div>
      </section>

      {/* ═══ ACT V: DATE BANDS ═════════════════════════════════════════ */}
      <section className="st-date-section relative min-h-screen flex flex-col justify-center py-32 overflow-hidden">
        <div className="absolute top-32 left-6 sm:left-12 text-[10px] uppercase tracking-[0.4em] opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          V — The Date
        </div>

        <div className="relative w-full">
          {/* DAY band — slides in from left */}
          <div className="st-date-day-band relative overflow-hidden flex items-center justify-between px-6 sm:px-12 py-6" style={{ background: "#0a0a0a", borderTop: "1px solid rgba(244, 236, 214, 0.1)", borderBottom: "1px solid rgba(244, 236, 214, 0.1)" }}>
            <p className="text-[10px] uppercase tracking-[0.5em] opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Day</p>
            <p className="font-light tabular-nums italic leading-none" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(4rem, 14vw, 12rem)" }}>
              {day}
            </p>
            <p className="text-[10px] uppercase tracking-[0.5em] opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>D</p>
          </div>

          {/* MONTH band — slides in from right */}
          <div className="st-date-month-band relative overflow-hidden flex items-center justify-between px-6 sm:px-12 py-6" style={{ background: "#1a1a1a", borderBottom: "1px solid rgba(244, 236, 214, 0.1)" }}>
            <p className="text-[10px] uppercase tracking-[0.5em] opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Month</p>
            <p className="font-light italic leading-none" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(4rem, 14vw, 12rem)" }}>
              {monthName.toLowerCase()}
            </p>
            <p className="text-[10px] uppercase tracking-[0.5em] opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace" }}>M</p>
          </div>

          {/* YEAR band — slides in from left, amber accent */}
          <div className="st-date-year-band relative overflow-hidden flex items-center justify-between px-6 sm:px-12 py-6" style={{ background: "rgba(212, 160, 78, 0.08)", borderBottom: "1px solid rgba(212, 160, 78, 0.3)" }}>
            <p className="text-[10px] uppercase tracking-[0.5em] opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#d4a04e" }}>Year</p>
            <p className="font-light italic tabular-nums leading-none" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(4rem, 14vw, 12rem)", color: "#d4a04e" }}>
              {year}
            </p>
            <p className="text-[10px] uppercase tracking-[0.5em] opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#d4a04e" }}>Y</p>
          </div>
        </div>

        <div className="st-date-meta mt-12 max-w-6xl mx-auto w-full grid grid-cols-2 sm:grid-cols-4 gap-6 px-6 sm:px-12 text-[10px] uppercase tracking-[0.4em]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          <div>
            <p className="opacity-50 mb-1">Weekday</p>
            <p>{weekday}</p>
          </div>
          <div>
            <p className="opacity-50 mb-1">Hour</p>
            <p className="tabular-nums">{time} CET</p>
          </div>
          <div>
            <p className="opacity-50 mb-1">Format</p>
            <p>Black-tie</p>
          </div>
          <div>
            <p className="opacity-50 mb-1">Reply</p>
            <p style={{ color: "#d4a04e" }}>Requested</p>
          </div>
        </div>
      </section>

      {/* ═══ ACT VI: REPLY ═════════════════════════════════════════════ */}
      <section className="st-form relative min-h-screen grid grid-cols-1 md:grid-cols-2 border-t" style={{ borderColor: "rgba(244, 236, 214, 0.1)" }}>
        <div className="absolute top-32 left-6 sm:left-12 text-[10px] uppercase tracking-[0.4em] opacity-60 z-10" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          VI — Reply
        </div>

        {/* Left: photo */}
        <div className="relative h-[40vh] md:h-auto overflow-hidden">
          <img src={galleryImages[3]} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(1) contrast(1.05)" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 text-white">
            <p className="italic font-light leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 3.5vw, 3rem)" }}>
              Your reply,<br />in strata.
            </p>
          </div>
        </div>

        {/* Right: form bands */}
        <div className="flex items-center justify-center px-6 sm:px-12 py-16 md:py-32">
          <div className="w-full max-w-md">
            <div className="st-form-row mb-6">
              <p className="text-[10px] uppercase tracking-[0.5em] mb-2 opacity-60" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#d4a04e" }}>
                Reply requested
              </p>
              <h2 className="italic font-light leading-tight mb-2" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 5vw, 4rem)" }}>
                Will you join us?
              </h2>
              <p className="italic text-lg opacity-70" style={{ fontFamily: "'Playfair Display', serif" }}>
                Before {(d ? d.getDate() - 14 : "—")} {monthName.toLowerCase()}.
              </p>
            </div>

            <div className="st-form-row">
              <WeddingProgram entries={program} className="mb-10" />
              {formSlot}
            </div>
          </div>
        </div>
      </section>

      <footer className="py-10 px-6 sm:px-12 text-[10px] uppercase tracking-[0.4em] opacity-50 border-t" style={{ borderColor: "rgba(244, 236, 214, 0.1)", fontFamily: "'JetBrains Mono', monospace" }}>
        <div className="max-w-7xl mx-auto grid grid-cols-3 items-baseline">
          <span>Strata · MMXXVI</span>
          <span className="text-center" style={{ color: "#d4a04e" }}>06 Acts</span>
          <span className="text-right">{firstName} &amp; {lastName}</span>
        </div>
      </footer>
    </main>
  );
}
